import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import { type Translate, useTranslation } from "@redotech/redo-i18n";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import { formatYearMonthLabel } from "@redotech/util/date-format";
import { memo, useMemo } from "react";
import { RedoButton } from "../../../buttons/redo-button";
import { Flex } from "../../../flex";
import type { RedoListItem } from "../../../list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../select-dropdown/redo-single-select-dropdown-input";
import { Text } from "../../../text";

interface CalendarHeaderProps {
  currentMonth: Temporal.PlainYearMonth;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSetMonth?: (month: Temporal.PlainYearMonth) => void;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
}

function monthNames(t: Translate): string[] {
  return [
    t("January", { context: "month name" }),
    t("February", { context: "month name" }),
    t("March", { context: "month name" }),
    t("April", { context: "month name" }),
    t("May", { context: "month name" }),
    t("June", { context: "month name" }),
    t("July", { context: "month name" }),
    t("August", { context: "month name" }),
    t("September", { context: "month name" }),
    t("October", { context: "month name" }),
    t("November", { context: "month name" }),
    t("December", { context: "month name" }),
  ];
}

function monthNamesShort(t: Translate): string[] {
  return [
    t("Jan", { context: "month abbreviation" }),
    t("Feb", { context: "month abbreviation" }),
    t("Mar", { context: "month abbreviation" }),
    t("Apr", { context: "month abbreviation" }),
    t("May", { context: "month abbreviation" }),
    t("Jun", { context: "month abbreviation" }),
    t("Jul", { context: "month abbreviation" }),
    t("Aug", { context: "month abbreviation" }),
    t("Sep", { context: "month abbreviation" }),
    t("Oct", { context: "month abbreviation" }),
    t("Nov", { context: "month abbreviation" }),
    t("Dec", { context: "month abbreviation" }),
  ];
}

function clampYearRange(
  currentYear: number,
  minDate?: Temporal.PlainDate,
  maxDate?: Temporal.PlainDate,
): { start: number; end: number } {
  const start = minDate?.year ?? currentYear - 10;
  const end = maxDate?.year ?? currentYear + 20;
  return {
    start: Math.min(start, currentYear),
    end: Math.max(end, currentYear),
  };
}

export const CalendarHeader = memo(function CalendarHeader({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onSetMonth,
  minDate,
  maxDate,
}: CalendarHeaderProps) {
  const { i18n, t } = useTranslation();
  const screenType = useScreenType(WMS_MOBILE_MAX_WIDTH);
  const isMobile = screenType === ScreenType.MOBILE;
  const monthItems: RedoListItem<number>[] = useMemo(() => {
    const names = isMobile ? monthNamesShort(t) : monthNames(t);
    return names.map((name, i) => ({
      id: String(i + 1),
      type: "text" as const,
      text: name,
      value: i + 1,
    }));
  }, [isMobile, t]);

  const yearItems: RedoListItem<number>[] = useMemo(() => {
    const { start, end } = clampYearRange(currentMonth.year, minDate, maxDate);
    const years: RedoListItem<number>[] = [];
    for (let y = start; y <= end; y++) {
      years.push({
        id: String(y),
        type: "text" as const,
        text: String(y),
        value: y,
      });
    }
    return years;
  }, [currentMonth.year, minDate, maxDate]);

  const selectedMonth = monthItems.find(
    (item) => item.value === currentMonth.month,
  );
  const selectedYear = yearItems.find(
    (item) => item.value === currentMonth.year,
  );

  const handleMonthPicked = ({
    item,
  }: {
    item: RedoListItem<number>;
  }): void => {
    if (!onSetMonth) {
      return;
    }
    onSetMonth(
      Temporal.PlainYearMonth.from({
        year: currentMonth.year,
        month: item.value,
      }),
    );
  };

  const handleYearPicked = ({ item }: { item: RedoListItem<number> }): void => {
    if (!onSetMonth) {
      return;
    }
    onSetMonth(
      Temporal.PlainYearMonth.from({
        year: item.value,
        month: currentMonth.month,
      }),
    );
  };

  return (
    <Flex align="center" dir="row" gap="xxs" justify="space-between" w="full">
      <RedoButton
        aria-label={t("Previous month", { context: "date picker" })}
        hierarchy="tertiary"
        IconLeading={ChevronLeftIcon}
        onClick={onPrevMonth}
        size={isMobile ? "sm" : "md"}
      />

      {onSetMonth ? (
        <Flex align="center" dir="row" gap="xxs">
          <div style={{ width: isMobile ? 94 : 150 }}>
            <RedoSingleSelectDropdownInput<number>
              fitToAnchor={false}
              options={monthItems}
              optionSelected={handleMonthPicked}
              selectedItem={selectedMonth}
              size={isMobile ? "xs" : "sm"}
            />
          </div>
          <div style={{ width: isMobile ? 76 : 110 }}>
            <RedoSingleSelectDropdownInput<number>
              fitToAnchor={false}
              options={yearItems}
              optionSelected={handleYearPicked}
              selectedItem={selectedYear}
              size={isMobile ? "xs" : "sm"}
            />
          </div>
        </Flex>
      ) : (
        <Text fontSize="md" fontWeight="semibold" textColor="secondary">
          {formatYearMonthLabel(currentMonth, i18n.language)}
        </Text>
      )}

      <RedoButton
        aria-label={t("Next month", { context: "date picker" })}
        hierarchy="tertiary"
        IconLeading={ChevronRightIcon}
        onClick={onNextMonth}
        size={isMobile ? "sm" : "md"}
      />
    </Flex>
  );
});
